import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Remembered view state: a search text, a filter, a tab. A page is mounted again when someone goes back to it
 * (back button, link), so plain component state would be lost. The value is kept for the browser session:
 * in memory (always) and in sessionStorage (survives a reload of the page, never reaches the server).
 * Nothing personal is stored beyond what the person typed into a filter field of the current session.
 */

const KEY_PREFIX = 'pz-view:';
const memory = new Map<string, unknown>();

function read<T>(key: string, fallback: T): T {
  if (memory.has(key)) return memory.get(key) as T;
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + key);
    if (raw !== null) {
      const value = JSON.parse(raw) as T;
      memory.set(key, value);
      return value;
    }
  } catch {
    /* storage not available or damaged: start from the default */
  }
  return fallback;
}

function write(key: string, value: unknown) {
  memory.set(key, value);
  try {
    sessionStorage.setItem(KEY_PREFIX + key, JSON.stringify(value));
  } catch {
    /* storage not available: the value stays in memory */
  }
}

function erase(key: string) {
  memory.delete(key);
  try {
    sessionStorage.removeItem(KEY_PREFIX + key);
  } catch {
    /* ignore */
  }
}

/** Forget every remembered view state (another person signs in, demo role switch). */
export function clearStickyState() {
  memory.clear();
  try {
    for (const k of Object.keys(sessionStorage)) if (k.startsWith(KEY_PREFIX)) sessionStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}

/**
 * useState that survives leaving and coming back to the page. `key` names the field ("projects.search");
 * returns [value, set, reset]. `reset` goes back to `initial` and forgets the stored value.
 */
export function useSticky<T>(key: string, initial: T): [T, (v: T | ((prev: T) => T)) => void, () => void] {
  const initialRef = useRef(initial);
  const [value, setValue] = useState<T>(() => read(key, initial));
  const latest = useRef(value);
  latest.current = value;
  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      const v = typeof next === 'function' ? (next as (prev: T) => T)(latest.current) : next;
      latest.current = v;
      write(key, v);
      setValue(v);
    },
    [key],
  );
  const reset = useCallback(() => {
    erase(key);
    latest.current = initialRef.current;
    setValue(initialRef.current);
  }, [key]);
  // another key (the same page for another object): read that entry
  const lastKey = useRef(key);
  useEffect(() => {
    if (lastKey.current === key) return;
    lastKey.current = key;
    const v = read(key, initialRef.current);
    latest.current = v;
    setValue(v);
  }, [key]);
  return [value, set, reset];
}

/** Scroll positions of the pages visited (by history entry), so that "back" lands where the person was. */
const scrollMemory = new Map<string, number>();
export const scrollPositions = {
  get: (key: string) => scrollMemory.get(key),
  set: (key: string, top: number) => {
    scrollMemory.set(key, top);
    // the list stays small: only the last 60 pages are kept
    if (scrollMemory.size > 60) scrollMemory.delete(scrollMemory.keys().next().value as string);
  },
};
